import offlineStorage from './OfflineStorage';
import onlineDetector from './OnlineDetector';
import conflictResolver, { ConflictStrategy } from './ConflictResolver';
import { ENTITY_API_MAP } from './db';
import { API_BASE_URL } from '../utils/constants';
import session from '../utils/session';
import logger from '../utils/logger';

const SYNC_INTERVAL_MS = 5 * 60 * 1000;
const MAX_RETRIES = 5;
const API_TIMEOUT = 20000;

class SyncEngine {
  constructor() {
    this._isSyncing = false;
    this._isInitialized = false;
    this._intervalId = null;
    this._syncListeners = new Set();
    this._lastSyncResult = null;
    this._onlineUnsub = null;
    this._conflictUnsub = null;
  }

  get isSyncing() {
    return this._isSyncing;
  }

  get lastSyncResult() {
    return this._lastSyncResult;
  }

  onSync(listener) {
    this._syncListeners.add(listener);
    return () => this._syncListeners.delete(listener);
  }

  init() {
    if (this._isInitialized) return;

    logger.info('[SyncEngine] Initializing', null, 'SyncEngine');

    onlineDetector.start();

    this._onlineUnsub = onlineDetector.onChange(async (online) => {
      if (online) {
        logger.info('[SyncEngine] Online detected, triggering sync', null, 'SyncEngine');
        await this.syncNow();
      }
    });

    this._startInterval();
    this._isInitialized = true;

    if (onlineDetector.isOnline) {
      this.syncNow().catch(() => {});
    }

    logger.info('[SyncEngine] Initialized', null, 'SyncEngine');
  }

  stop() {
    if (!this._isInitialized) return;

    this._stopInterval();
    if (this._onlineUnsub) { this._onlineUnsub(); this._onlineUnsub = null; }
    onlineDetector.stop();
    this._isInitialized = false;
    logger.info('[SyncEngine] Stopped', null, 'SyncEngine');
  }

  async syncNow() {
    if (this._isSyncing) {
      logger.debug('[SyncEngine] Already syncing, skipping', null, 'SyncEngine');
      return { synced: 0, failed: 0, conflicts: 0, total: 0, skipped: true };
    }

    if (!onlineDetector.isOnline) {
      logger.debug('[SyncEngine] Offline, skipping sync', null, 'SyncEngine');
      return { synced: 0, failed: 0, conflicts: 0, total: 0, offline: true };
    }

    const userData = session.getUser();
    if (!userData) {
      logger.debug('[SyncEngine] No user session, skipping sync', null, 'SyncEngine');
      return { synced: 0, failed: 0, conflicts: 0, total: 0, noAuth: true };
    }

    this._isSyncing = true;
    this._emitSyncStart();

    let synced = 0;
    let failed = 0;
    let conflicts = 0;

    try {
      const pendingItems = await offlineStorage.getPendingQueue();

      if (pendingItems.length === 0) {
        this._lastSyncResult = { synced: 0, failed: 0, conflicts: 0, total: 0 };
        this._emitSyncComplete(this._lastSyncResult);
        return this._lastSyncResult;
      }

      const operations = pendingItems.map(item => ({
        localId: item.localId,
        entityType: item.entityType,
        action: item.action,
        serverId: item.serverId,
        data: item.data,
        clientVersion: item.version || 1,
        clientTimestamp: item.createdAt || Date.now(),
      }));

      const results = await this._sendBatch(operations);

      for (let i = 0; i < results.length; i++) {
        const result = results[i];
        const queueItem = pendingItems[i];

        if (result.success) {
          await offlineStorage.markSynced(queueItem, result.serverId, result.serverVersion, result.resolvedId);
          await offlineStorage.removeFromQueue(queueItem.id);
          synced++;
        } else if (result.error === 'VERSION_CONFLICT') {
          const localRecord = await offlineStorage.getById(queueItem.entityType, queueItem.localId);
          const conflict = conflictResolver.detectConflict(localRecord, result.serverData);

          if (conflict.hasConflict) {
            const resolvedData = await conflictResolver.resolve(conflict, ConflictStrategy.SERVER_WINS);
            if (resolvedData) {
              await offlineStorage.save(queueItem.entityType, resolvedData);
              await offlineStorage.removeFromQueue(queueItem.id);
              synced++;
            } else {
              conflicts++;
            }
          }
        } else if (result.shouldRetry !== false && (queueItem.retryCount || 0) < MAX_RETRIES) {
          await offlineStorage.updateQueueItem(queueItem.id, {
            status: 'pending',
            retryCount: (queueItem.retryCount || 0) + 1,
            lastError: result.error,
          });
          failed++;
        } else {
          await offlineStorage.updateQueueItem(queueItem.id, {
            status: 'failed',
            retryCount: (queueItem.retryCount || 0) + 1,
            lastError: result.error,
          });
          failed++;
        }
      }
    } catch (err) {
      logger.error('[SyncEngine] Sync error', err, 'SyncEngine');
      failed = -1;
    } finally {
      this._isSyncing = false;
      this._lastSyncResult = { synced, failed, conflicts, total: synced + failed + conflicts };
      this._emitSyncComplete(this._lastSyncResult);
    }

    return this._lastSyncResult;
  }

  async _sendBatch(operations) {
    const url = `${API_BASE_URL.replace(/\/$/, '')}/sync/batch`;

    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), API_TIMEOUT);

      const headers = {
        'Content-Type': 'application/json',
      };
      const token = session.getToken();
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await fetch(url, {
        method: 'POST',
        headers,
        credentials: 'include',
        body: JSON.stringify({ operations }),
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        const errorBody = await response.text();
        logger.error(`[SyncEngine] Batch sync HTTP ${response.status}`, errorBody, 'SyncEngine');
        throw new Error(`HTTP ${response.status}: ${errorBody}`);
      }

      const result = await response.json();
      return result.results || [];
    } catch (err) {
      if (err.name === 'AbortError') {
        logger.warn('[SyncEngine] Batch sync timed out', null, 'SyncEngine');
      }
      throw err;
    }
  }

  async retryFailed() {
    const owner = offlineStorage.getCurrentOwner();
    if (!owner) return;

    const all = await offlineStorage.getPendingQueue();
    for (const item of all) {
      if (item.status === 'failed') {
        await offlineStorage.updateQueueItem(item.id, {
          status: 'pending',
          retryCount: 0,
          lastError: null,
        });
      }
    }

    return this.syncNow();
  }

  _startInterval() {
    if (this._intervalId) return;
    this._intervalId = setInterval(() => {
      if (onlineDetector.isOnline && !this._isSyncing) {
        this.syncNow().catch(() => {});
      }
    }, SYNC_INTERVAL_MS);
  }

  _stopInterval() {
    if (this._intervalId) {
      clearInterval(this._intervalId);
      this._intervalId = null;
    }
  }

  _emitSyncStart() {
    for (const listener of this._syncListeners) {
      try { listener({ type: 'start' }); } catch {}
    }
  }

  _emitSyncComplete(result) {
    for (const listener of this._syncListeners) {
      try { listener({ type: 'complete', result }); } catch {}
    }
  }
}

const syncEngine = new SyncEngine();
export default syncEngine;
